/**
 * Canonical comparison of machine stitch lists. Formats differ in what they can store
 * (VP3 has no jumps, JEF has no trims, PEC attaches trims to the next jump, trailing trims
 * are meaningless), so both sides are reduced to a canonical form before comparing.
 */
import { t } from '../i18n/i18n';
import { CMD } from '../engine/stitch/sequence';
import type { MachineStitch } from './machine';

export interface CanonicalOptions {
  /** Jumps are not stored; following stitch positions carry the movement (VP3). */
  dropJumps?: boolean;
  /** Trims are not stored (JEF). */
  dropTrims?: boolean;
}

export interface Canonical {
  moves: { x: number; y: number; jump: boolean }[];
  /** Move counts at which a trim occurs (deduplicated, trailing trims removed). */
  trims: number[];
  /** Move counts at which a colour change occurs. */
  colors: number[];
}

export function canonical(stitches: MachineStitch[], opts: CanonicalOptions = {}): Canonical {
  const moves: Canonical['moves'] = [];
  const trims: number[] = [];
  const colors: number[] = [];
  for (const s of stitches) {
    if (s.cmd === CMD.STITCH) moves.push({ x: s.x, y: s.y, jump: false });
    else if (s.cmd === CMD.JUMP) {
      if (!opts.dropJumps) moves.push({ x: s.x, y: s.y, jump: true });
    } else if (s.cmd === CMD.TRIM) {
      if (!opts.dropTrims && trims[trims.length - 1] !== moves.length) trims.push(moves.length);
    } else if (s.cmd === CMD.COLOR_CHANGE || s.cmd === CMD.STOP) colors.push(moves.length);
    else if (s.cmd === CMD.END) break;
  }
  while (trims.length && trims[trims.length - 1] >= moves.length) trims.pop();
  return { moves, trims, colors };
}

export interface CompareResult {
  equal: boolean;
  differences: string[];
}

export function compareCanonical(expected: Canonical, actual: Canonical): CompareResult {
  const differences: string[] = [];
  if (expected.moves.length !== actual.moves.length) {
    differences.push(t('number of moves: expected {e}, found {a}', { e: expected.moves.length, a: actual.moves.length }));
  }
  const n = Math.min(expected.moves.length, actual.moves.length);
  for (let i = 0; i < n; i++) {
    const a = expected.moves[i], b = actual.moves[i];
    if (a.x !== b.x || a.y !== b.y || a.jump !== b.jump) {
      differences.push(t('move {i}: expected ({ex}, {ey}{ej}), found ({ax}, {ay}{aj})', { i, ex: a.x, ey: a.y, ej: a.jump ? ', ' + t('jump') : '', ax: b.x, ay: b.y, aj: b.jump ? ', ' + t('jump') : '' }));
      break;
    }
  }
  if (expected.colors.join(',') !== actual.colors.join(',')) differences.push(t('colour changes differ ({e} vs {a})', { e: expected.colors.length, a: actual.colors.length }));
  if (expected.trims.join(',') !== actual.trims.join(',')) differences.push(t('trims differ ({e} vs {a})', { e: expected.trims.length, a: actual.trims.length }));
  return { equal: differences.length === 0, differences };
}
